import CartService, { CART_EVENTS } from '../cart/cart-service'
import {
  initiateAuth,
  handleAuthCallback,
  getValidAccessToken,
  getStoredToken,
  tryRefreshToken,
  clearToken,
  isAuthInProgress,
} from './customer-token'
import {
  readCartMetafield,
  writeCartMetafield,
  hasPurchasedSince,
  AuthError,
} from './metafield-service'

let syncDebounceTimer = null

// Set on gift card lines sent to a recipient (see product-form.js)
const GIFT_RECIPIENT_PROPERTY = '__shopify_send_gift_card_to_recipient'

function hasGiftRecipient(properties) {
  return Boolean(properties && properties[GIFT_RECIPIENT_PROPERTY])
}

function getTechnicalData() {
  try {
    const el = document.querySelector('[data-technical]')
    return el ? JSON.parse(el.textContent) : {}
  } catch (e) {
    return {}
  }
}

async function getCurrentCartItems() {
  return new Promise((resolve) => {
    fetch('/cart.js')
      .then((r) => r.json())
      .then((cart) => {
        // Merge duplicate variant lines, preserving properties from first line
        const map = {}
        ;(cart.items || []).forEach((item) => {
          if (map[item.variant_id]) {
            map[item.variant_id].quantity += item.quantity
          } else {
            map[item.variant_id] = {
              variantId: item.variant_id,
              quantity: item.quantity,
              properties: item.properties?._cart_label
              ? { _cart_label: item.properties._cart_label }
              : {},
            }
          }
        })
        resolve(Object.values(map))
      })
      .catch(() => resolve([]))
  })
}

async function consolidateCart() {
  const cart = await fetch('/cart.js').then((r) => r.json())
  const byVariant = {}
  let hasDupes = false

  ;(cart.items || []).forEach((item) => {
    // Gift card lines sent to a recipient can each carry their own
    // recipient info - never merge them together even if they share a
    // variant ID. Physical/self-purchase gift cards have no recipient
    // properties and can be merged like anything else.
    if (hasGiftRecipient(item.properties)) return
    if (byVariant[item.variant_id]) {
      byVariant[item.variant_id].extras.push(item)
      hasDupes = true
    } else {
      byVariant[item.variant_id] = { keep: item, extras: [] }
    }
  })

  if (!hasDupes) return false

  const changes = Object.values(byVariant)
    .filter(({ extras }) => extras.length > 0)
    .flatMap(({ keep, extras }) => {
      const totalQty = extras.reduce(
        (sum, i) => sum + i.quantity, keep.quantity,
      )
      return [
        { id: keep.key, quantity: totalQty },
        ...extras.map((e) => ({ id: e.key, quantity: 0 })),
      ]
    })

  // Sequential to avoid Shopify cart race conditions with parallel mutations
  await changes.reduce(
    (seq, change) => seq.then(() => fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(change),
    })),
    Promise.resolve(),
  )
  return true
}

async function applyRemoteCart(targetItems, hasSynced) {
  await consolidateCart()
  const current = await getCurrentCartItems()
  const targetMap = {}
  targetItems.forEach((i) => { targetMap[i.variantId] = i.quantity })
  const currentMap = {}
  current.forEach((i) => { currentMap[i.variantId] = i.quantity })

  const toAdd = targetItems.filter((i) => !currentMap[i.variantId])

  const toUpdate = targetItems.filter(
    (i) => currentMap[i.variantId] !== undefined
      && currentMap[i.variantId] !== i.quantity,
  )

  const toRemove = hasSynced
    ? current.filter((i) => !targetMap[i.variantId])
    : []

  if (toAdd.length > 0) {
    const toAddPayload = (items) => JSON.stringify({
      items: items.map((i) => ({
        id: i.variantId,
        quantity: i.quantity,
        properties: i.properties || {},
      })),
    })
    const addResp = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: toAddPayload(toAdd),
    })
    if (!addResp.ok && toAdd.length > 1) {
      // Batch failed (likely one OOS item) — retry individually, skip failures
      await Promise.allSettled(toAdd.map((i) => fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: toAddPayload([i]),
      })))
    }
  }

  if (toUpdate.length > 0 || toRemove.length > 0) {
    const updates = {}
    toUpdate.forEach((i) => { updates[i.variantId] = i.quantity })
    toRemove.forEach((i) => { updates[i.variantId] = 0 })
    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates }),
    })
  }

  if (toAdd.length > 0 || toUpdate.length > 0 || toRemove.length > 0) {
    await CartService.get(true)
  }
}

function mergeItemLists(local, remote) {
  const localMap = {}
  local.forEach((i) => {
    localMap[i.variantId] = {
      quantity: (localMap[i.variantId]?.quantity || 0) + i.quantity,
      properties: i.properties || {},
    }
  })
  const remoteMap = {}
  remote.forEach((i) => {
    remoteMap[i.variantId] = {
      quantity: (remoteMap[i.variantId]?.quantity || 0) + i.quantity,
      properties: i.properties || {},
    }
  })

  const allIds = new Set([
    ...Object.keys(localMap),
    ...Object.keys(remoteMap),
  ])

  return Array.from(allIds).map((id) => {
    const localEntry = localMap[id]
    const remoteEntry = remoteMap[id]
    const localQty = localEntry?.quantity || 0
    const remoteQty = remoteEntry?.quantity || 0
    const quantity = localQty > 0 && remoteQty > 0
      ? localQty + remoteQty
      : localQty || remoteQty
    const properties = localEntry?.properties || remoteEntry?.properties || {}
    return { variantId: Number(id), quantity, properties }
  })
}

function scheduleSyncWrite(shopId, getToken, clientId, cartMetafieldKey) {
  clearTimeout(syncDebounceTimer)
  syncDebounceTimer = setTimeout(async () => {
    const items = await getCurrentCartItems()
    let token = getToken()
    if (!token) {
      token = await tryRefreshToken(shopId, clientId)
      if (!token) return
    }
    try {
      await writeCartMetafield(shopId, token, cartMetafieldKey, items)
    } catch (e) {
      if (!(e instanceof AuthError)) return
      const refreshed = await tryRefreshToken(shopId, clientId)
      if (refreshed) {
        await writeCartMetafield(shopId, refreshed, cartMetafieldKey, items)
      }
    }
  }, 1500)
}

export default async function initCartSync() {
  const {
    customerLoggedIn,
    cartMetafieldKey,
    shopId,
    customerAccountClientId,
  } = getTechnicalData()

  if (!customerAccountClientId || !shopId || !cartMetafieldKey) return

  const urlParams = new URLSearchParams(window.location.search)
  const isCallback = urlParams.has('code') && urlParams.has('state')

  const redirectUri = `${window.location.origin}/`

  if (isCallback) {
    const result = await handleAuthCallback(
      shopId,
      customerAccountClientId,
      redirectUri,
    )

    if (result?.needsFullAuth) {
      await initiateAuth(shopId, customerAccountClientId, redirectUri, true)
      return
    }

    const token = result

    if (token) {
      try {
        const saved = await readCartMetafield(
          shopId,
          token.accessToken,
          cartMetafieldKey,
        )
        const savedItems = saved?.items || []
        const hasSynced = saved !== null
        const localItems = await getCurrentCartItems()
        // Remote has items but local is empty: might be post-purchase elsewhere
        if (hasSynced && localItems.length === 0 && savedItems.length > 0) {
          const purchased = await hasPurchasedSince(
            shopId, token.accessToken, saved.updatedAt,
          )
          if (purchased) {
            await writeCartMetafield(
              shopId, token.accessToken, cartMetafieldKey, [],
            )
            return
          }
        }

        // hasSynced: local = pre-logout state + offline additions
        // → keep local as-is, only add remote items absent from local
        // hasSynced=false: first-ever sync → add quantities from both
        const mergedItems = hasSynced
          ? [
              ...localItems,
              ...savedItems.filter(
                (i) => !localItems.some((l) => l.variantId === i.variantId),
              ),
            ]
          : mergeItemLists(localItems, savedItems)
        await applyRemoteCart(mergedItems, hasSynced)
        await writeCartMetafield(
          shopId,
          token.accessToken,
          cartMetafieldKey,
          mergedItems,
        )
      } catch (e) {
        // sync failed silently
      }
    }

    // Callback landed on the correct page already — no redirect needed
    return
  }

  if (!customerLoggedIn) return
  if (isAuthInProgress()) return

  let accessToken = getValidAccessToken(shopId)

  if (!accessToken) {
    const stored = getStoredToken(shopId)
    if (stored?.refreshToken) {
      accessToken = await tryRefreshToken(shopId, customerAccountClientId)
      if (!accessToken) {
        clearToken()
        await initiateAuth(shopId, customerAccountClientId, redirectUri)
        return
      }
    } else {
      await initiateAuth(shopId, customerAccountClientId, redirectUri)
      return
    }
  }

  async function runSync(token) {
    const saved = await readCartMetafield(shopId, token, cartMetafieldKey)
    const savedItems = saved?.items || []
    const hasSynced = saved !== null
    const localItems = await getCurrentCartItems()

    if (hasSynced && saved.updatedAt) {
      const lastWrite = parseInt(
        localStorage.getItem('_tmw_cart_sync_last_write') || '0',
        10,
      )
      if (saved.updatedAt <= lastWrite) {
        // We wrote this metafield — local cart is source of truth
        // (e.g. checkout corrected quantities since our last write)
        await writeCartMetafield(
          shopId, token, cartMetafieldKey, localItems,
        )
        return
      }
    }

    // Remote is newer + local empty: might be post-purchase on another device
    if (hasSynced && localItems.length === 0 && savedItems.length > 0) {
      const purchased = await hasPurchasedSince(
        shopId, token, saved.updatedAt,
      )
      if (purchased) {
        await writeCartMetafield(shopId, token, cartMetafieldKey, [])
        return
      }
    }

    const targetItems = hasSynced
      ? savedItems
      : mergeItemLists(localItems, savedItems)
    await applyRemoteCart(targetItems, hasSynced)
    const finalItems = await getCurrentCartItems()
    await writeCartMetafield(shopId, token, cartMetafieldKey, finalItems)
  }

  try {
    await runSync(accessToken)
  } catch (e) {
    if (!(e instanceof AuthError)) return
    const refreshed = await tryRefreshToken(shopId, customerAccountClientId)
    if (!refreshed) {
      clearToken()
      await initiateAuth(shopId, customerAccountClientId, redirectUri)
      return
    }
    try {
      await runSync(refreshed)
    } catch (e2) {
      clearToken()
    }
  }

  let cartConsolidating = false

  CartService.addListener(async (event) => {
    if (event !== CART_EVENTS.FINISHED || cartConsolidating) return
    localStorage.setItem('_tmw_cart_local_change', String(Date.now()))
    cartConsolidating = true
    const didConsolidate = await consolidateCart()
    cartConsolidating = false
    if (didConsolidate) await CartService.get(true)
    scheduleSyncWrite(
      shopId,
      () => getValidAccessToken(shopId),
      customerAccountClientId,
      cartMetafieldKey,
    )
  })

  document.addEventListener('openMiniCart', async () => {
    const token = getValidAccessToken(shopId)
    if (!token) return
    try {
      const saved = await readCartMetafield(shopId, token, cartMetafieldKey)
      if (!saved) return
      const localItems = await getCurrentCartItems()
      const localMap = {}
      localItems.forEach((i) => { localMap[i.variantId] = i.quantity })
      const remoteMap = {}
      saved.items.forEach((i) => { remoteMap[i.variantId] = i.quantity })
      const isDifferent = saved.items.some(
        (i) => localMap[i.variantId] !== i.quantity,
      ) || localItems.some((i) => !remoteMap[i.variantId])
      if (!isDifferent) return
      const lastChange = parseInt(
        localStorage.getItem('_tmw_cart_local_change') || '0',
        10,
      )
      if (saved.updatedAt > lastChange) {
        await applyRemoteCart(saved.items, true)
      }
    } catch (e) {
      // sync failed silently — drawer opens with cached state
    }
  }, { capture: true })
}
